import React from "react";
import Facebook from "@mui/icons-material/Facebook";
import YouTube from "@mui/icons-material/YouTube";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { resolveSiteSocialUrl, SITE_SOCIAL_LINKS } from "../../config/siteSocialLinks";
import { resolveAppSupportPath, resolveAppMarketingPath, APP_SITE_ID } from "../../config/appSiteLinks";
import { usePagesTranslation } from "../locales";
import { ACCENT, SURFACE, TEXT } from "../../dark-theme/palette";

const YOUTUBE_CHANNEL_URL = resolveSiteSocialUrl(
    process.env.REACT_APP_YOUTUBE_CHANNEL_URL,
    SITE_SOCIAL_LINKS.youtube
);
const FACEBOOK_URL = resolveSiteSocialUrl(
    process.env.REACT_APP_FACEBOOK_URL,
    SITE_SOCIAL_LINKS.facebook
);
const INSTAGRAM_URL = resolveSiteSocialUrl(
    process.env.REACT_APP_INSTAGRAM_URL,
    SITE_SOCIAL_LINKS.instagram
);

const MUTED_LINK_SX = {
    color: "rgba(0, 0, 0, 0.45)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    textDecoration: "none",
    lineHeight: 1.4,
    transition: "color 0.15s ease",
    "&:hover": {
        color: "#3BA666",
        textDecoration: "none",
    },
};

/** Same footer on the site theme's backdrop, inverted for contrast. */
const DARK_TONE = {
    link: { color: TEXT.secondary, "&:hover": { color: ACCENT.light } },
    border: `1px solid ${SURFACE.border}`,
    copyright: TEXT.disabled,
};

const LINK_ROW_SX = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: { xs: 1.25, sm: 2 },
    rowGap: 1,
};

function InstagramIcon() {
    return (
        <Box
            component="svg"
            viewBox="0 0 24 24"
            aria-hidden
            sx={{ width: 18, height: 18, display: "block" }}
        >
            <path
                fill="currentColor"
                d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 11-2.881.001 1.44 1.44 0 012.881-.001z"
            />
        </Box>
    );
}

function ExternalSocialLink({ href, label, icon, linkSx }) {
    return (
        <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                ...linkSx,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
            }}
        >
            {icon ? (
                <Box component="span" aria-hidden sx={{ display: "inline-flex", color: "inherit" }}>
                    {icon}
                </Box>
            ) : null}
            {label}
        </Link>
    );
}

/**
 * About / Contact / Privacy / Terms / Support / Marketing + copyright.
 * `tone="dark"` inverts the link colours for pages on the night slate.
 */
export default function SiteMarketingFooter({ tone = "light" }) {
    const { t } = usePagesTranslation();
    const dark = tone === "dark";
    const linkSx = dark ? { ...MUTED_LINK_SX, ...DARK_TONE.link } : MUTED_LINK_SX;

    const socialLinks = [
        FACEBOOK_URL && {
            key: "facebook",
            href: FACEBOOK_URL,
            label: t("landing.facebook"),
            icon: <Facebook sx={{ fontSize: 18 }} />,
        },
        INSTAGRAM_URL && {
            key: "instagram",
            href: INSTAGRAM_URL,
            label: t("landing.instagram"),
            icon: <InstagramIcon />,
        },
        YOUTUBE_CHANNEL_URL && {
            key: "youtube",
            href: YOUTUBE_CHANNEL_URL,
            label: t("landing.youtube"),
            icon: <YouTube sx={{ fontSize: 18 }} />,
        },
    ].filter(Boolean);

    const policyLinks = [
        { key: "about", to: "/about", label: t("landing.about-us") },
        { key: "stories", to: "/stories", label: t("stories.page-title", "Stories") },
        { key: "contact", to: "/contact", label: t("landing.contact", "Contact Us") },
        { key: "privacy", to: "/privacy", label: t("landing.privacy-policy") },
        { key: "terms", to: "/terms", label: t("landing.terms-of-service") },
        { key: "support", to: resolveAppSupportPath(APP_SITE_ID.WEB), label: t("landing.support") },
        { key: "marketing", to: resolveAppMarketingPath(APP_SITE_ID.WEB), label: t("landing.marketing") },
    ];

    return (
        <Box
            component="footer"
            data-site-chrome="footer"
            sx={{
                mt: { xs: 5, sm: 6 },
                pt: { xs: 3, sm: 4 },
                pb: { xs: 2.5, sm: 3 },
                px: { xs: 1, sm: 0 },
                borderTop: dark ? DARK_TONE.border : "1px solid rgba(0, 0, 0, 0.08)",
                textAlign: "center",
            }}
        >
            {socialLinks.length > 0 ? (
                <Box sx={{ ...LINK_ROW_SX, mb: 2 }}>
                    {socialLinks.map((item) => (
                        <ExternalSocialLink
                            key={item.key}
                            href={item.href}
                            label={item.label}
                            icon={item.icon}
                            linkSx={linkSx}
                        />
                    ))}
                </Box>
            ) : null}

            <Box sx={{ ...LINK_ROW_SX, mb: 2 }}>
                {policyLinks.map((item) => (
                    <Link key={item.key} component={RouterLink} to={item.to} sx={linkSx}>
                        {item.label}
                    </Link>
                ))}
            </Box>

            <Typography
                variant="body2"
                sx={{
                    mt: 1,
                    color: dark ? DARK_TONE.copyright : "rgba(0, 0, 0, 0.38)",
                    fontSize: "0.75rem",
                    letterSpacing: "0.01em",
                }}
            >
                {t("landing.copyright")}
            </Typography>
        </Box>
    );
}
